import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './DevicePicker.css';

/**
 * DevicePicker - Select AWS Device Farm device for remote access testing
 * Supports pagination to load more devices if initial batch is busy
 */
function DevicePicker({ platform, onDeviceSelect, selectedDeviceArn }) {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');
    const [nextToken, setNextToken] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        if (platform) {
            // Reset state when platform changes
            setDevices([]);
            setNextToken(null);
            setHasMore(false);
            loadDevices();
        }
    }, [platform]);

    const loadDevices = async (token = null) => {
        if (token) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const params = { platform };
            if (token) {
                params.nextToken = token;
            }

            const response = await api.get('/devices/aws-devices', { params });

            if (token) {
                // Append to existing devices
                setDevices(prev => [...prev, ...(response.data.devices || [])]);
            } else {
                // Replace devices (initial load)
                setDevices(response.data.devices || []);
            }

            setNextToken(response.data.nextToken);
            setHasMore(response.data.hasMore);
        } catch (err) {
            console.error('Failed to load devices:', err);
            setError('Failed to load Device Farm devices');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        if (nextToken && !loadingMore) {
            loadDevices(nextToken);
        }
    };

    const filteredDevices = devices.filter(device =>
        device.name.toLowerCase().includes(filter.toLowerCase()) ||
        device.model.toLowerCase().includes(filter.toLowerCase()) ||
        device.os.toLowerCase().includes(filter.toLowerCase())
    );

    // Count available vs busy devices
    const availableCount = devices.filter(d => d.availability === 'AVAILABLE').length;
    const busyCount = devices.length - availableCount;

    if (loading) {
        return (
            <div className="device-picker">
                <div className="loading">Loading devices...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="device-picker">
                <div className="error">{error}</div>
                <button onClick={() => loadDevices()} className="btn btn-secondary">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="device-picker">
            <div className="device-picker-header">
                <h4>Select Physical Device</h4>
                <input
                    type="text"
                    placeholder="Search devices..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="device-search"
                />
            </div>

            {/* Device stats summary */}
            <div className="device-stats">
                <span className="stat-available">{availableCount} available</span>
                <span className="stat-busy">{busyCount} busy</span>
                <span className="stat-total">{devices.length} loaded</span>
            </div>

            <div className="device-list">
                {/* Auto-selection option */}
                <div
                    className={`device-item auto-select ${selectedDeviceArn === 'auto' ? 'selected' : ''}`}
                    onClick={() => onDeviceSelect({
                        arn: 'auto',
                        name: 'Auto (pick best device)',
                        model: 'Automatic Selection',
                        os: 'Best Available',
                        platform: platform,
                        availability: 'AVAILABLE',
                        provider: 'aws'
                    })}
                >
                    <div className="device-info">
                        <div className="device-name">🤖 Auto (pick best device)</div>
                        <div className="device-details">
                            <span className="device-model">Automatically selects the best available device</span>
                        </div>
                    </div>
                    <div className="device-status">
                        <span className="status-auto">✨ Recommended</span>
                    </div>
                </div>

                {platform?.toUpperCase() === 'ANDROID' && (
                    <div
                        className={`device-item emulator-select ${selectedDeviceArn === 'emulator' ? 'selected' : ''}`}
                        onClick={() => onDeviceSelect({
                            arn: 'emulator',
                            name: 'Android Emulator (Standard)',
                            model: 'Virtual Emulator',
                            os: 'Android 14 (Standard)',
                            platform: platform,
                            availability: 'AVAILABLE',
                            provider: 'emulator'
                        })}
                    >
                        <div className="device-info">
                            <div className="device-name">🤖 Android Emulator (Standard)</div>
                            <div className="device-details">
                                <span className="device-model">Virtual Emulator</span>
                            </div>
                        </div>
                        <div className="device-status">
                            <span className="status-available">✓ Available</span>
                        </div>
                    </div>
                )}

                {/* Divider */}
                {filteredDevices.length > 0 && (
                    <div className="device-list-divider">
                        <span>Or choose manually</span>
                    </div>
                )}

                {filteredDevices.length === 0 ? (
                    <div className="no-devices">
                        No devices found {filter && `matching "${filter}"`}
                    </div>
                ) : (
                    filteredDevices.map(device => (
                        <div
                            key={device.arn}
                            className={`device-item ${selectedDeviceArn === device.arn ? 'selected' : ''}`}
                            onClick={() => onDeviceSelect(device)}
                        >
                            <div className="device-info">
                                <div className="device-name">{device.name}</div>
                                <div className="device-details">
                                    <span className="device-model">{device.model}</span>
                                    <span className="device-os">{device.os}</span>
                                    {device.formFactor && (
                                        <span className="device-form">{device.formFactor}</span>
                                    )}
                                </div>
                                {device.resolution && (
                                    <div className="device-resolution">
                                        {device.resolution.width}x{device.resolution.height}
                                    </div>
                                )}
                            </div>
                            <div className="device-status">
                                {device.availability === 'AVAILABLE' ? (
                                    <span className="status-available">✓ Available</span>
                                ) : (
                                    <span className="status-busy">Busy</span>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Load More button */}
            {hasMore && (
                <div className="load-more-container">
                    <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="btn btn-secondary load-more-btn"
                    >
                        {loadingMore ? 'Loading...' : 'Load More Devices'}
                    </button>
                    {busyCount > 0 && availableCount === 0 && (
                        <p className="load-more-hint">
                            All loaded devices are busy. Load more to find available devices.
                        </p>
                    )}
                </div>
            )}

            {selectedDeviceArn && (
                <div className="selected-device-info">
                    <strong>Selected:</strong> {devices.find(d => d.arn === selectedDeviceArn)?.name}
                </div>
            )}
        </div>
    );
}

export default DevicePicker;

